//! dropdown

"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowRight, ChevronRight } from "lucide-react";
import { EXPLORE_PANEL, SOLUTIONS } from "@/utils/header/dropDownData";
import Image from "next/image";
import { useRouter } from "next/navigation";

const MENU = {
  Solutions: SOLUTIONS,
};

export default function DropdownItem({
  title,
  isMobile = false,
  activeMenu,
  setActiveMenu,
  closeMenu,
}) {
  const menu = MENU[title];

  const isOpen = activeMenu === title;
  const open = () => setActiveMenu?.(title);
  const close = () => setActiveMenu?.((m) => (m === title ? null : m));

  const [activeCategoryId, setActiveCategoryId] = useState(
    menu?.categories[0]?.id ?? null,
  );
  const router = useRouter();
  const [isMobileView, setIsMobileView] = useState(false);
  const [isTabletView, setIsTabletView] = useState(false);
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setIsMobileView(w <= 767);
      setIsTabletView(w >= 768 && w <= 1015);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!menu) return null;

  const isHoverable = !isMobileView && !isTabletView;
  const categories = menu.categories;
  const activeCategory =
    categories.find((c) => c.id === activeCategoryId) ?? categories[0];

  // Delayed close lets the mouse bridge the gap between trigger and dropdown.
  const handleOpen = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    open();
  };
  const handleCloseSoon = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => close(), 150);
  };

  return (
    <div
      ref={rootRef}
      onMouseEnter={isHoverable ? handleOpen : undefined}
      onMouseLeave={isHoverable ? handleCloseSoon : undefined}
      className={`relative ${isMobile ? "w-full" : "w-auto"}`}
    >
      <button
        onClick={(e) => {
          e.preventDefault();
          setActiveMenu?.((m) => (m === title ? null : title));
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={
          isMobile
            ? "btn-ghost flex items-center gap-1 text-[#292929]"
            : "btn-ghost flex items-center gap-1 "
        }
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* DESKTOP MEGA MENU
       */}
      {isHoverable && isOpen && (
        <div
          onMouseEnter={handleOpen}
          onMouseLeave={handleCloseSoon}
          className="fixed left-1/2 top-[70px] z-[10001] -translate-x-1/2 pt-2"
          style={{ width: "min(1100px, calc(100vw - 2rem))" }}
        >
          <div className="rounded-3xl border border-gray-100 bg-white  shadow-[0_25px_60px_rgba(0,0,0,0.12)]">
            <div className="grid grid-cols-12 gap-">
              {/* COLUMN 1 — categories */}
              <div className="col-span-4 p-4">
                <p className="px-3 pb-2 text-sm font-medium text-gray-400">
                  {menu.sectionLabel}
                </p>
                <div className="space-y-1">
                  {categories.map((cat) => {
                    const Icon = cat.Icon;
                    const active = activeCategory.id === cat.id;

                    return (
                      <div
                        key={cat.id}
                        onClick={() => {
                          router.push(cat.href);
                          close();
                        }}
                        onMouseEnter={() => setActiveCategoryId(cat.id)}
                        className={`group flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-3 transition-colors ${
                          active ? "bg-gray-100" : "hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
                            active
                              ? "border-gray-200 bg-white"
                              : "border-gray-100 bg-gray-50"
                          }`}
                        >
                          <Icon
                            strokeWidth={1.5}
                            className="h-4 w-4 text-[#606060]"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {cat.title}
                          </p>
                          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                            {cat.description}
                          </p>
                        </div>

                        <div className="group-hover:opacity-100 transition duration-200 group-hover:translate-x-2 opacity-0 ml-auto flex h-8 w-8 shrink-0 items-center justify-center">
                          <ArrowRight size={16} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* COLUMN 2 — sub-items of the active category */}
              <div className="col-span-4 border-l border-black/10 pt-4 ">
                <div className=" flex justify-between border-b border-black/10 px-2 items-center">
                  <p className="px-3 pb-2 text-sm font-medium text-gray-400">
                    Features
                  </p>
                  <Link
                    href={activeCategory.href ?? "#"}
                    onClick={() => close()}
                    className="group"
                  >
                    <p className=" pb-1 text-xs font-semibold flex items-center text-[#0D00E5] ">
                      Learn More{" "}
                      <ChevronRight
                        size={16}
                        className="group-hover:translate-x-1 transition-all duration-300"
                      />
                    </p>
                  </Link>
                </div>

                <div className="sub-items space-y-1 h-[270px]  px-2 pt-2 overflow-y-auto">
                  {(activeCategory.items?.length
                    ? activeCategory.items
                    : [
                        {
                          id: activeCategory.id,
                          title: activeCategory.title,
                          description: activeCategory.description,
                          // href: activeCategory.href,
                          Icon: activeCategory.Icon,
                        },
                      ]
                  ).map((item, idx) => {
                    const Icon = item.Icon;
                    return (
                      <div
                        key={item.id ?? idx}
                        className="group flex items-center gap-3 rounded-2xl px-3  py-3 transition-colors hover:bg-gray-50"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-gray-50 group-hover:bg-white">
                          <Icon
                            strokeWidth={1.5}
                            className="h-4 w-4 text-[#606060]"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {item.title}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* COLUMN 3 — explore / featured */}

              <div className="col-span-4 border-l border-black/10 p-4">
                <div className="flex justify-between items-center ">
                  <p className="text-sm font-medium text-gray-400">
                    {EXPLORE_PANEL.title}
                  </p>
                  <Link
                    href={EXPLORE_PANEL.ctaHref}
                    onClick={() => close()}
                    className="group inline-flex items-center gap-1 text-xs font-semibold text-[#0D00E5] hover:text-blue-700"
                  >
                    {EXPLORE_PANEL.ctaLabel}
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-all duration-300"
                    />
                  </Link>
                </div>
                <div className="col-span-4 rounded-2xl bg-gradient-to-b from-[#EBF1FF] to-[#F9FAFE] mt-2 p-4">
                  <div className="space-y-4">
                    {EXPLORE_PANEL.items.map((item, idx) => (
                      <Link
                        key={item.id ?? idx}
                        href={item.href}
                        onClick={() => close()}
                        className="group flex items-start gap-3"
                      >
                        <div className="relative border h-[65px] w-[65px] shrink-0 overflow-hidden rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                          <Image
                            src={item.image}
                            alt={item.title}
                            width={60}
                            height={60}
                            className="w-[65px] h-[65px] object-contain  rounded-lg group-hover:scale-105 transition-transform duration-300 ease-in-out "
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-1 text-sm font-semibold text-gray-900 group-hover:text-blue-600">
                            {item.title}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs leading-2 text-gray-500">
                            {item.description}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE / TABLET — accordion of SECTIONS only. The features inside each
          section are intentionally hidden here; tapping a section navigates to
          its page where the features live. */}
      {(isMobile || isTabletView) && isOpen && (
        <div className="mt-2 space-y-1 bg-white pl-3">
          {categories.map((cat) => {
            const Icon = cat.Icon;
            return (
              <Link
                key={cat.id}
                href={cat.href ?? "#"}
                onClick={() => {
                  close();
                  closeMenu?.();
                }}
                className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-gray-50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-gray-50">
                  <Icon strokeWidth={1.5} className="h-4 w-4 text-[#606060]" />
                </div>
                <div>
                  <p className="text-[15px] font-medium text-gray-900">
                    {cat.title}
                  </p>
                  <p className="text-xs text-gray-500">{cat.description}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
